import {
  MigrateToV2,
  AhaEditorUtils,
  type EditorPlugin,
  type AhaSlateEditor,
  type DocumentValue,
  type DocumentValueV1,
  slate,
} from '@aha-app/aha-editor-v2';
import type ApplicationController from '@aha-app/mvc';
import { Page, type NoteTemplate } from 'components/framework/models';
import { TEMPLATE_LOCAL_STORAGE_KEY } from 'components/page_templates/TemplateController';
import type { GlobalNoteTemplate } from 'components/page_templates/types';
import { BEST_PRACTICES_PANEL_KEY } from 'components/whiteboards/best_practices_panel/BestPracticesPanel';
import {
  getLocalStoragePreference,
  removeLocalStoragePreference,
} from 'javascripts/utils';
import { cloneDeep } from 'lodash';

export interface AhaControllerWithSidePanel extends ApplicationController {
  actionShowSidePanel(
    options: { open: false } | { open: true; panel: string }
  ): void;
  initializeSidePanels(): void;
}

export interface AhaDocumentTemplatePluginProps {
  controller: AhaControllerWithSidePanel;
  pageId?: RecordId;
  usedTemplateIds: string[];
}

type DocumentTemplate = NoteTemplate | GlobalNoteTemplate;

interface AhaDocumentTemplateAPI {
  onAddTemplate(template: DocumentTemplate, isGlobal: boolean): void;
  insertExampleContent(editor: AhaSlateEditor, content: Object): void;
  getUsedTemplateIds(): string[];
}

export type AhaDocumentTemplatePlugin = EditorPlugin<AhaDocumentTemplateAPI>;

export const AHA_DOCUMENT_TEMPLATE_PLUGIN_ID = 'documentTemplate';

const AhaDocumentTemplatePluginFactory = ({
  controller,
  pageId,
  usedTemplateIds: initialUsedTemplateIds,
}: AhaDocumentTemplatePluginProps): AhaDocumentTemplatePlugin => {
  let usedTemplateIds = initialUsedTemplateIds || [];
  let reactivePage: Page | null = null;

  const checkForTemplate = () => {
    // Check to see if the user just created a note from a global template
    const templateId = getLocalStoragePreference(TEMPLATE_LOCAL_STORAGE_KEY);

    if (
      templateId &&
      usedTemplateIds.length === 1 &&
      usedTemplateIds[0] === templateId
    ) {
      controller.actionShowSidePanel({
        open: true,
        panel: BEST_PRACTICES_PANEL_KEY,
      });
      removeLocalStoragePreference(TEMPLATE_LOCAL_STORAGE_KEY);
    }
  };

  if (pageId && !reactivePage) {
    reactivePage = new Page({ id: pageId });

    reactivePage.reactive({
      query: () =>
        Page.select('id').merge({ description: ['usedTemplateIds'] }),
      reload: true,
      onUpdate: page => {
        if (page.description.usedTemplateIds?.length > 0) {
          usedTemplateIds = page.description.usedTemplateIds;
          controller.initializeSidePanels();
        }
      },
    });
  }

  const resetSidePanels = ({ forceOpen }: { forceOpen: boolean }): void => {
    controller.initializeSidePanels();

    if (forceOpen) {
      controller.actionShowSidePanel({
        open: true,
        panel: BEST_PRACTICES_PANEL_KEY,
      });
    } else {
      checkForTemplate();
    }
  };

  return {
    id: AHA_DOCUMENT_TEMPLATE_PLUGIN_ID,
    name: 'Document Template Plugin',
    handlers: {
      onInit: () => {
        checkForTemplate();
      },
    },
    api: {
      onAddTemplate(template: DocumentTemplate, isGlobal: boolean): void {
        if (isGlobal && template.id) {
          if (!usedTemplateIds.includes(template.id)) {
            usedTemplateIds.push(template.id);
          }

          resetSidePanels({ forceOpen: true });
        }
      },
      insertExampleContent(editor: AhaSlateEditor, content: Object): void {
        let checkedContent: DocumentValueV1 | DocumentValue | null = null;
        if ('children' in content) {
          checkedContent = content as DocumentValue;
        } else if ('document' in content) {
          checkedContent = content as DocumentValueV1;
        }

        if (checkedContent) {
          const fragment = MigrateToV2(checkedContent);
          // Move to the end of the document, insert a new paragraph, and then insert the fragment's children.
          AhaEditorUtils.Selection.moveToEnd(editor);
          AhaEditorUtils.Transforms.insertNodes(editor, {
            type: 'paragraph',
            children: [{ text: '' }],
          });
          slate.Transforms.insertFragment(editor, cloneDeep(fragment.children));
        }
      },
      getUsedTemplateIds(): string[] {
        return usedTemplateIds;
      },
    },
  };
};

export default AhaDocumentTemplatePluginFactory;
